import { isEmpty, noop, padStart } from 'lodash'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import Select from '../Select'
import { TSelect } from '../Select/types'
import {
  parseTime,
  parseTimeWithSeconds,
  serializeTime,
  stringifyTimeWithSeconds,
} from './helpers'
import TimePickerDate from './TimePickerDate'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`
type TTimeWrapper = {
  customWidth?: string
}

const TimeWrapper = styled.div<TTimeWrapper>`
  width: ${({ customWidth }) => customWidth || '50px'};
`

export type TTimePicker = {
  value?: string
  onChange?: (data: string) => void
  preText?: string
  enableSeconds?: boolean
  customWidth?: string
  minTime?: string
  maxTime?: string
} & TSelect

const TimePicker = (props: TTimePicker): React.ReactElement => {
  const {
    value = '00:00',
    onChange = noop,
    preText = '',
    enableSeconds = false,
    customWidth = undefined,
    minTime = undefined,
    maxTime = undefined,
    ...rest
  } = props

  const [hour, setHour] = useState<string>('00')
  const [minute, setMinute] = useState<string>('00')
  const [second] = useState<string>('00')

  const isValidTime = (currentValue: string, valueType = 'hour'): boolean => {
    if (!isEmpty(minTime) && !isEmpty(maxTime)) {
      return true
    }

    const { hour } = parseTime(value)

    const { hour: minHour } =
      minTime && !isEmpty(minTime) ? parseTime(minTime) : parseTime('00:00')
    const { hour: maxHour } =
      maxTime && !isEmpty(maxTime) ? parseTime(maxTime) : parseTime('23:59')

    if (valueType === 'hour') {
      if (minHour <= currentValue && maxHour >= currentValue) {
        return true
      }
      if (minHour <= currentValue && isEmpty(maxHour)) {
        return true
      }
      if (isEmpty(minHour) && maxHour >= currentValue) {
        return true
      }
      return false
    }
    if (valueType === 'minute') {
      const valueTime = enableSeconds
        ? `${hour}:${currentValue}:${second}`
        : `${hour}:${currentValue}`
      const durationValue = moment.duration(valueTime).asMilliseconds()
      const maxDuration = !isEmpty(maxTime)
        ? moment.duration(maxTime).asMilliseconds()
        : 0
      const minDuration = !isEmpty(minTime)
        ? moment.duration(minTime).asMilliseconds()
        : 0

      if (durationValue >= minDuration && durationValue <= maxDuration) {
        return true
      }
      if (isEmpty(minTime) && durationValue <= maxDuration) {
        return true
      }
      if (durationValue >= minDuration && isEmpty(maxTime)) {
        return true
      }

      return false
    }
    return false
  }

  const hourOptions = useMemo(() => {
    const hourOptions: { value: string; label: string }[] = []
    for (let i = 0; i < 24; i++) {
      const value = padStart(String(i), 2, '0')
      if (isValidTime(value, 'hour')) {
        hourOptions.push({
          label: value,
          value,
        })
      }
    }
    return hourOptions
  }, [minTime, maxTime, value])

  const minuteOptions = useMemo(() => {
    const minuteOptions: { value: string; label: string }[] = []
    for (let i = 0; i < 60; i++) {
      const value = padStart(String(i), 2, '0')
      if (isValidTime(value, 'minute')) {
        minuteOptions.push({
          label: value,
          value,
        })
      }
    }

    return minuteOptions
  }, [minTime, maxTime, value])

  useEffect(() => {
    if (!value || (enableSeconds && value === '00:00')) {
      enableSeconds ? onChange(`00:00:00`) : onChange(`00:00`)
    } else {
      const parsed = parseTime(value)
      setHour(parsed.hour.toString())
      setMinute(parsed.minute.toString())
    }
  }, [value])

  const onChangeHour = (value: string): void => {
    enableSeconds
      ? onChange(`${value}:${minute}:${second}`)
      : onChange(`${value}:${minute}`)
  }

  const onChangeMinute = (value: string): void => {
    enableSeconds
      ? onChange(`${hour}:${value}:${second}`)
      : onChange(`${hour}:${value}`)
  }

  return (
    <Wrapper>
      {preText && <span className="mr-1">{`${preText}`}</span>}

      <TimeWrapper customWidth={customWidth}>
        <Select
          {...rest}
          options={hourOptions}
          slim
          direct
          value={hour}
          onChange={onChangeHour}
        />
      </TimeWrapper>
      <div style={{ padding: '0 5px' }}>:</div>
      <TimeWrapper customWidth={customWidth}>
        <Select
          {...rest}
          options={minuteOptions}
          slim
          direct
          value={minute}
          onChange={onChangeMinute}
        />
      </TimeWrapper>
    </Wrapper>
  )
}

export default TimePicker

export {
  parseTime,
  serializeTime,
  parseTimeWithSeconds,
  stringifyTimeWithSeconds,
  TimePickerDate,
}
